import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { Modal, Table, Transfer } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { Key, useEffect, useState } from 'react';
import { TransferSearch } from './TransferSearch';

type TransferItem = GetProp<TransferProps, 'dataSource'>[number];
type TableRowSelection<T extends object> = TableProps<T>['rowSelection'];

export interface TransferDataSourceInterface {
  id: number;
  key: number;
  name: string;
  disabled: boolean;
  type?: 'series' | 'season' | 'content';
}

export interface TransferDataHandlerInterface {
  fetchSourceData: (
    query: Record<string, string>
  ) => Promise<{ data: TransferDataSourceInterface[]; count: number }>;
  fetchTargetData: (
    query: Record<string, string>
  ) => Promise<{ data: TransferDataSourceInterface[]; count: number }>;
  createWithParentId: (ids: number[]) => Promise<void>;
  deleteWithParentId: (ids: number[]) => Promise<void>;
}

interface TableTransferProps<T extends TransferDataSourceInterface>
  extends TransferProps<TransferItem> {
  dataHandler: TransferDataHandlerInterface;
  columns: TableColumnsType<T>;
  size?: 'small' | 'middle' | 'large';
  filterOptions?: { label: string; value: string }[];
  onFilterChange?: (value: string) => void;
  filterValue?: string;
}

const CustomTransfer = <T extends TransferDataSourceInterface>(
  props: TableTransferProps<T>
): React.ReactElement => {
  const {
    columns,
    dataHandler,
    size = 'large',
    filterOptions,
    onFilterChange,
    filterValue,
    ...restProps
  } = props;
  const [pageSize, setPageSize] = useState(10);
  const [leftSelectedKeys, setLeftSelectedKeys] = useState<number[]>([]);
  const [rightSelectedKeys, setRightSelectedKeys] = useState<number[]>([]);

  const [transferDataSource, setTransferDataSource] = useState<TransferDataSourceInterface[]>([]);
  const [transferDataSourceTotal, setTransferDataSourceTotal] = useState(0);
  const [targetSource, setTargetSource] = useState<TransferDataSourceInterface[]>([]);
  const [targetSourceTotal, setTargetSourceTotal] = useState(0);

  const [targetPage, setTargetPage] = React.useState<number>(1);
  const [sourcePage, setSourcePage] = React.useState<number>(1);

  const [targetSearchTerm, setTargetSearchTerm] = React.useState<string>('');
  const [sourceSearchTerm, setSourceSearchTerm] = React.useState<string>('');

  const [targetContentType, setTargetContentType] = React.useState<string>(
    'tv-series,movie,clip,trailer'
  );
  const [sourceContentType, setSourceContentType] = React.useState<string>(
    'tv-series,movie,clip,trailer'
  );

  const [targetOrder, setTargetOrder] = React.useState<string>('id');
  const [sourceOrder, setSourceOrder] = React.useState<string>('id');

  const [targetSortField, setTargetSortField] = React.useState<Key>('');
  const [sourceSortField, setSourceSortField] = React.useState<Key>('');

  const [isSourceLoading, setIsSourceLoading] = React.useState(false);
  const [isTargetLoading, setIsTargetLoading] = React.useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const onPageChange = async (
    direction: 'left' | 'right',
    page: number,
    pageSize: number,
    filters: Record<string, FilterValue | null>,
    sorter?: SorterResult<TransferDataSourceInterface>
  ): Promise<void> => {
    setPageSize(pageSize);
    const contentType: string = (filters.content_type as unknown as string) || 'tv-series,movie';
    if (direction === 'right') {
      setTargetPage(page);
      setTargetContentType(contentType);

      if (sorter && sorter.field) {
        setTargetOrder(sorter.order || '');
        setTargetSortField(Array.isArray(sorter.field) ? sorter.field[0] : sorter.field);
      }
      return;
    } else {
      setSourcePage(page);
      setSourceContentType(contentType);

      if (sorter && sorter.field) {
        setSourceOrder(sorter.order || '');
        setSourceSortField(Array.isArray(sorter.field) ? sorter.field[0] : sorter.field);
      }
    }
  };

  const onSearchHandler = async (direction: 'left' | 'right', value: string): Promise<void> => {
    if (direction === 'right') {
      setTargetSearchTerm(value);
      setTargetPage(1);
    } else {
      setSourceSearchTerm(value);
      setSourcePage(1);
    }
  };

  const onChange: TransferProps['onChange'] = async (
    _nextTargetKeys: TransferProps['targetKeys'],
    direction: string
  ) => {
    const isMovingRight = direction === 'right';
    const confirmMessage = isMovingRight
      ? 'Would you like to register the selected items?'
      : 'Would you like to unregister the selected items?';

    Modal.confirm({
      title: 'Confirm',
      content: confirmMessage,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          if (isMovingRight) {
            await dataHandler.createWithParentId(leftSelectedKeys as number[]);
            setLeftSelectedKeys([]);
          } else {
            await dataHandler.deleteWithParentId(rightSelectedKeys as number[]);
            setTargetPage(1);
            setRightSelectedKeys([]);
          }
          setRefreshKey(prev => prev + 1);
        } catch (error) {
          // message.error(`Failed to update the items`);
          console.error(error);
          setRefreshKey(prev => prev + 1);
        }
      },
    });
  };

  useEffect(() => {
    const currentSelectedKeys = leftSelectedKeys;
    const query = {
      page: sourcePage.toString(),
      page_size: pageSize.toString(),
      search: sourceSearchTerm,
      content_type: sourceContentType,
      order_by: sourceSortField?.toString() || 'id',
      order_direction: sourceOrder === 'ascend' ? 'asc' : 'desc',
    };
    setIsSourceLoading(true);
    dataHandler
      .fetchSourceData(query)
      .then(({ data, count }) => {
        setTransferDataSource(data);
        setTransferDataSourceTotal(count);
      })
      .finally(() => {
        setIsSourceLoading(false);
      });
    setLeftSelectedKeys(currentSelectedKeys);
  }, [
    sourcePage,
    sourceSearchTerm,
    pageSize,
    refreshKey,
    sourceContentType,
    sourceOrder,
    sourceSortField,
  ]);

  useEffect(() => {
    const currentSelectedKeys = rightSelectedKeys;
    const query = {
      page: targetPage.toString(),
      page_size: pageSize.toString(),
      search: targetSearchTerm,
      content_type: targetContentType,
      order_by: targetSortField.toString(),
      order_direction: targetOrder === 'ascend' ? 'asc' : 'desc',
    };
    setIsTargetLoading(true);
    dataHandler
      .fetchTargetData(query)
      .then(({ data, count }) => {
        setTargetSource(data);
        setTargetSourceTotal(count);
      })
      .finally(() => {
        setIsTargetLoading(false);
      });
    setTransferDataSource([...transferDataSource, ...targetSource]);
    setRightSelectedKeys(currentSelectedKeys);
  }, [
    targetPage,
    targetSearchTerm,
    pageSize,
    refreshKey,
    targetContentType,
    targetOrder,
    targetSortField,
  ]);

  return (
    <Transfer
      className="transfer-container"
      {...restProps}
      dataSource={transferDataSource.map(item => ({
        key: item.id,
        title: item.name,
        disabled: item.disabled,
      }))}
      targetKeys={targetSource.map(item => item.id)}
      selectedKeys={[...leftSelectedKeys, ...rightSelectedKeys]}
      onSearch={onSearchHandler}
      onChange={onChange}
      showSelectAll={false}
      selectAllLabels={[' ', ' ']}
      titles={[
        `${leftSelectedKeys.length}/${transferDataSourceTotal} Total Items`,
        `${rightSelectedKeys.length}/${targetSourceTotal} Registered Items`,
      ]}
    >
      {({ direction, onItemSelectAll, disabled: listDisabled }) => {
        const filteredDataSource = direction === 'left' ? transferDataSource : targetSource;
        const total = direction === 'left' ? transferDataSourceTotal : targetSourceTotal;
        const currentSelectedKeys = direction === 'left' ? leftSelectedKeys : rightSelectedKeys;
        const rowSelection: TableRowSelection<TransferItem> = {
          getCheckboxProps: () => ({ disabled: listDisabled }),
          onChange(selectedRowKeys) {
            const currentPageItems = filteredDataSource.map(item => item.id);

            if (direction === 'left') {
              // 현재 페이지에 없는 선택된 항목들 유지
              const otherPagesSelected = leftSelectedKeys.filter(
                key => !currentPageItems.includes(key)
              );

              // 현재 페이지의 선택된 항목들만 포함
              const newLeftKeys = [
                ...otherPagesSelected,
                ...selectedRowKeys.filter(key => currentPageItems.includes(key as number)),
              ];

              setLeftSelectedKeys(newLeftKeys as number[]);

              // Transfer 내부 상태와 동기화
              const currentPageSelectedKeys = selectedRowKeys.filter(key =>
                currentPageItems.includes(key as number)
              );
              onItemSelectAll(currentPageSelectedKeys, currentPageSelectedKeys.length > 0);
            } else {
              const otherPagesSelected = rightSelectedKeys.filter(
                key => !currentPageItems.includes(key)
              );

              const newRightKeys = [
                ...otherPagesSelected,
                ...selectedRowKeys.filter(key => currentPageItems.includes(key as number)),
              ];
              setTransferDataSource([...transferDataSource, ...targetSource]);
              setRightSelectedKeys(newRightKeys as number[]);

              // Transfer 내부 상태와 동기화
              const currentPageSelectedKeys = selectedRowKeys.filter(key =>
                currentPageItems.includes(key as number)
              );
              onItemSelectAll(currentPageSelectedKeys, currentPageSelectedKeys.length > 0);
            }
          },
          selectedRowKeys: currentSelectedKeys.filter(key =>
            filteredDataSource.some(item => item.id === key)
          ),
          selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
        };

        return (
          <>
            <TransferSearch
              direction={direction}
              handleSearch={onSearchHandler}
              filterOptions={filterOptions}
              onFilterChange={onFilterChange}
              filterValue={filterValue}
            />
            <Table
              loading={direction === 'left' ? isSourceLoading : isTargetLoading}
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredDataSource}
              size={size}
              pagination={{
                current: direction === 'left' ? sourcePage : targetPage,
                pageSize: pageSize,
                total: total,
                // onChange: (page, pageSize) => {
                //   onPageChange(direction, page, pageSize);
                // },
              }}
              onChange={(pagination, filters, sorter) => {
                onPageChange(
                  direction,
                  pagination.current || 1,
                  pagination.pageSize || 10,
                  filters,
                  sorter as SorterResult<TransferDataSourceInterface>
                );
              }}
              style={{ pointerEvents: listDisabled ? 'none' : undefined }}
              onRow={() => ({
                onClick: (): void => {
                  // onItemSelect(id, listSelectedKeys.includes(id));
                },
              })}
            />
          </>
        );
      }}
    </Transfer>
  );
};

export default CustomTransfer;
